import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

import { ManufacturerType } from '../enums/other-filters.enums';

export interface OtherFiltersProps {
  manufacturer_type_enabled: boolean;
  manufacturer_type: ManufacturerType | null;
  expected_shortage_date: boolean;
}

const initialProps: OtherFiltersProps = {
  manufacturer_type_enabled: false,
  manufacturer_type: ManufacturerType.OTHER_MANUFACTURERS,
  expected_shortage_date: false,
};

const STORE_NAME = 'sandoz-v2-other-filters';

@Injectable({ providedIn: 'root' })
export class OtherFiltersRepository {
  private readonly store;

  public readonly manufacturerTypeEnabled$: Observable<boolean>;
  public readonly manufacturerType$: Observable<ManufacturerType | null>;
  public readonly expectedShortageDate$: Observable<boolean>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<OtherFiltersProps>(initialProps),
    );

    this.manufacturerTypeEnabled$ = this.store.pipe(
      select(state => state.manufacturer_type_enabled),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.manufacturerType$ = this.store.pipe(
      select(state => state.manufacturer_type),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.expectedShortageDate$ = this.store.pipe(
      select(state => state.expected_shortage_date),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  resetStore(): void {
    this.store.reset();
  }

  getState(): OtherFiltersProps {
    return this.store.getValue();
  }

  getStoreProp<K extends keyof OtherFiltersProps>(
    key: K,
  ): OtherFiltersProps[K] {
    return this.store.getValue()[key];
  }

  setManufacturerTypeEnabled(
    enabled: OtherFiltersProps['manufacturer_type_enabled'],
  ): void {
    this.store.update(setProp('manufacturer_type_enabled', enabled));
  }

  setManufacturerType(type: OtherFiltersProps['manufacturer_type']): void {
    this.store.update(setProp('manufacturer_type', type));
  }

  setExpectedShortageDate(
    value: OtherFiltersProps['expected_shortage_date'],
  ): void {
    this.store.update(setProp('expected_shortage_date', value));
  }
}
